import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { artifactAPI, userAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import NotificationBell from '../components/NotificationBell';
import { toast } from 'sonner';
import {
  Zap, Home, Menu, LogOut, Plus, Trash2, CreditCard, Box, Circle, Watch,
  Tag, QrCode, Package, Users, Copy, ExternalLink, Search, Filter, Settings,
  BarChart3, ShoppingBag, ClipboardList
} from 'lucide-react';

const ARTIFACT_TYPES = [
  { value: 'card', label: 'NFC Card', icon: <CreditCard className="w-4 h-4" /> },
  { value: 'cube', label: 'QR Cube', icon: <Box className="w-4 h-4" /> },
  { value: 'ring', label: 'Smart Ring', icon: <Circle className="w-4 h-4" /> },
  { value: 'bracelet', label: 'Bracelet', icon: <Watch className="w-4 h-4" /> },
  { value: 'pendant', label: 'Pendant', icon: <Tag className="w-4 h-4" /> },
  { value: 'keychain', label: 'Keychain', icon: <QrCode className="w-4 h-4" /> },
];

const AdminArtifactsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [artifacts, setArtifacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Create form
  const [createType, setCreateType] = useState('card');
  const [createCount, setCreateCount] = useState(1);
  const [createLabelPrefix, setCreateLabelPrefix] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [artifactsRes, usersRes] = await Promise.all([
        artifactAPI.adminList(),
        userAPI.list(),
      ]);
      setArtifacts(artifactsRes.data);
      setUsers(usersRes.data.filter(u => u.role !== 'super_admin'));
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkCreate = async () => {
    if (createCount < 1 || createCount > 100) {
      toast.error('Count must be between 1 and 100');
      return;
    }
    setCreating(true);
    try {
      const res = await artifactAPI.adminBulkCreate({
        artifact_type: createType,
        count: createCount,
        label_prefix: createLabelPrefix || null,
      });
      setArtifacts([...res.data, ...artifacts]);
      setShowCreate(false);
      setCreateCount(1);
      setCreateLabelPrefix('');
      toast.success(`${res.data.length} artifact(s) created!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create artifacts');
    } finally {
      setCreating(false);
    }
  };

  const handleAssignUser = async (artifactId, userId) => {
    try {
      await artifactAPI.adminAssign(artifactId, userId);
      const u = users.find(u => u.user_id === userId);
      setArtifacts(artifacts.map(a =>
        a.artifact_id === artifactId ? { ...a, user_id: userId, status: 'active', assigned_user_name: u?.name } : a
      ));
      toast.success(`Artifact assigned to ${u?.name}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign');
    }
  };

  const handleDelete = async (artifactId) => {
    if (!window.confirm('Delete this artifact? This cannot be undone.')) return;
    try {
      await artifactAPI.adminDelete(artifactId);
      setArtifacts(artifacts.filter(a => a.artifact_id !== artifactId));
      toast.success('Artifact deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const filtered = artifacts.filter(a => {
    if (filterStatus === 'unassigned' && a.user_id) return false;
    if (filterStatus === 'assigned' && !a.user_id) return false;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      return (a.artifact_id?.toLowerCase().includes(s) || a.label?.toLowerCase().includes(s) || a.assigned_user_name?.toLowerCase().includes(s) || a.nfc_token?.toLowerCase().includes(s));
    }
    return true;
  });

  const stats = {
    total: artifacts.length,
    assigned: artifacts.filter(a => a.user_id).length,
    unassigned: artifacts.filter(a => !a.user_id).length,
  };

  const navItems = [
    { icon: <Home className="w-5 h-5" />, label: 'Admin', path: '/admin' },
    { icon: <Package className="w-5 h-5" />, label: 'Artifacts', path: '/admin/manage-artifacts', active: true },
    { icon: <ShoppingBag className="w-5 h-5" />, label: 'Catalog', path: '/admin/artifacts' },
    { icon: <ClipboardList className="w-5 h-5" />, label: 'Orders', path: '/admin/orders' },
    { icon: <Users className="w-5 h-5" />, label: 'Users', path: '/admin' },
    { icon: <BarChart3 className="w-5 h-5" />, label: 'Analytics', path: '/analytics' },
    { icon: <Settings className="w-5 h-5" />, label: 'Settings', path: '/settings' },
  ];

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="spinner" /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <aside className={`dashboard-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="p-6 border-b border-slate-200">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center"><Zap className="w-5 h-5 text-white" /></div>
            <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Signa</span>
          </Link>
        </div>
        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {navItems.map((item, i) => (
              <li key={i}>
                <Link to={item.path} className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${location.pathname === item.path ? 'bg-indigo-50 text-indigo-600' : 'text-slate-600 hover:bg-slate-100'}`}>
                  {item.icon}<span className="font-medium">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-3 px-4 py-2 mb-2">
            <div className="w-9 h-9 bg-indigo-100 rounded-full flex items-center justify-center"><span className="text-indigo-600 font-semibold text-sm">{user?.name?.charAt(0).toUpperCase()}</span></div>
            <div className="flex-1 min-w-0"><p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p><p className="text-xs text-slate-500 truncate">{user?.email}</p></div>
          </div>
          <Button variant="ghost" className="w-full justify-start text-slate-600" onClick={() => { logout(); navigate('/'); }}><LogOut className="w-5 h-5 mr-3" />Sign out</Button>
        </div>
      </aside>

      <div className="dashboard-content">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-6 h-16">
            <button className="lg:hidden p-2 text-slate-600" onClick={() => setSidebarOpen(true)}><Menu className="w-6 h-6" /></button>
            <h1 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Manage Artifacts</h1>
            <div className="flex items-center gap-3">
              <NotificationBell />
              <Button onClick={() => setShowCreate(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="bulk-create-btn">
                <Plus className="w-4 h-4 mr-2" />Bulk Create
              </Button>
            </div>
          </div>
        </header>

        <main className="p-6" data-testid="admin-artifacts-page">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            {[
              { label: 'Total', value: stats.total, color: 'bg-indigo-50 text-indigo-700' },
              { label: 'Assigned', value: stats.assigned, color: 'bg-emerald-50 text-emerald-700' },
              { label: 'Unassigned', value: stats.unassigned, color: 'bg-amber-50 text-amber-700' },
            ].map(s => (
              <div key={s.label} className={`rounded-xl p-4 ${s.color}`} data-testid={`stat-${s.label.toLowerCase()}`}>
                <p className="text-2xl font-bold">{s.value}</p>
                <p className="text-sm font-medium opacity-80">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Bulk Create Form */}
          {showCreate && (
            <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6" data-testid="bulk-create-form">
              <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Bulk Create Artifacts</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <Label>Type</Label>
                  <Select value={createType} onValueChange={setCreateType}>
                    <SelectTrigger className="mt-1" data-testid="create-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ARTIFACT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Count</Label>
                  <Input type="number" min={1} max={100} className="mt-1" value={createCount} onChange={e => setCreateCount(parseInt(e.target.value) || 1)} data-testid="create-count-input" />
                </div>
                <div>
                  <Label>Label Prefix (optional)</Label>
                  <Input className="mt-1" placeholder="e.g. Batch-A" value={createLabelPrefix} onChange={e => setCreateLabelPrefix(e.target.value)} data-testid="create-label-input" />
                </div>
                <div className="flex items-end gap-2">
                  <Button onClick={handleBulkCreate} disabled={creating} className="bg-indigo-600 hover:bg-indigo-700" data-testid="confirm-create-btn">
                    {creating ? 'Creating...' : `Create ${createCount}`}
                  </Button>
                  <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input className="pl-10" placeholder="Search by ID, label, or user..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="search-input" />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40" data-testid="filter-select"><Filter className="w-4 h-4 mr-2" /><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="assigned">Assigned</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Artifacts List */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Artifact</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Type</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">URL</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Assigned To</th>
                    <th className="text-left px-4 py-3 font-medium text-slate-600">Status</th>
                    <th className="text-right px-4 py-3 font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map(a => {
                    const typeInfo = ARTIFACT_TYPES.find(t => t.value === a.artifact_type);
                    return (
                      <tr key={a.artifact_id} className="hover:bg-slate-50" data-testid={`admin-artifact-${a.artifact_id}`}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-slate-900">{a.label || typeInfo?.label}</p>
                          <p className="text-xs text-slate-400 font-mono">{a.artifact_id}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-slate-100 text-slate-700 text-xs font-medium">
                            {typeInfo?.icon} {typeInfo?.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <code className="text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded font-mono max-w-[200px] truncate">{a.tap_url}</code>
                            <button onClick={() => copyToClipboard(a.tap_url)} className="text-slate-400 hover:text-slate-600" data-testid={`copy-url-${a.artifact_id}`}>
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                            <a href={a.tap_url} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-indigo-600">
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          </div>
                          <p className="text-xs text-slate-400 font-mono mt-0.5">{a.nfc_token}</p>
                        </td>
                        <td className="px-4 py-3">
                          {a.user_id ? (
                            <span className="text-sm text-slate-700 font-medium">{a.assigned_user_name || a.user_id}</span>
                          ) : (
                            <Select onValueChange={(val) => handleAssignUser(a.artifact_id, val)}>
                              <SelectTrigger className="h-8 w-40 text-xs" data-testid={`assign-user-${a.artifact_id}`}>
                                <SelectValue placeholder="Assign user..." />
                              </SelectTrigger>
                              <SelectContent>
                                {users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.email})</SelectItem>)}
                              </SelectContent>
                            </Select>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${a.user_id ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                            {a.user_id ? 'Assigned' : 'Unassigned'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {!a.user_id && (
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(a.artifact_id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-artifact-${a.artifact_id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {filtered.length === 0 && (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No artifacts found</p>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminArtifactsPage;
